import { pipe, Option } from "fp";
import React from "react";

import * as Driver from "~/Driver";
import { HoursOfService } from "~/HoursOfService";

import { Order } from "~/Order";
import * as Tooltip from "~/Tooltip";

export type Timer = { readonly timer: keyof HoursOfService.Timers["timers"] };
export function Timer({
  hoursOfService,
  summary,
  timer,
  ...datum
}: Driver.Table.Datum & Timer) {
  const noBreak = pipe(
    hoursOfService?.type,
    Option.fromNullable,
    Option.chain(
      Option.fromPredicate(
        (type) => timer === "break" && !HoursOfService.Type.hasBreak({ type })
      )
    ),
    Option.map(() => (
      <Tooltip.Component
        Content={() => (
          <>Breaks are not required by this driver's current HoS rule.</>
        )}
      >
        <span>N/A</span>
      </Tooltip.Component>
    )),
    Option.toUndefined
  );

  const duration = (
    <HoursOfService.Timer
      {...(hoursOfService?.timers?.[timer] ?? { timer: { duration: 0 } })}
    />
  );

  const tooltip = summary?.issues.violations[timer] == true && (
    <Tooltip.Component
      Content={() => (
        <>
          <Driver.Name.Component noLink {...datum} />
          &nbsp;currently has a {timer} violation
        </>
      )}
    >
      <span>{duration}</span>
    </Tooltip.Component>
  );

  return (
    <Driver.Table.Clickable onClick={Driver.Table.useLogLink(datum).go}>
      {noBreak || tooltip || duration}
    </Driver.Table.Clickable>
  );
}

export namespace Timer {
  export const column = (title: string, timer: Timer) => ({
    ...Settings.basics(title, timer),
    ...Settings.sorting(timer),
    ...Settings.rendering(timer),
  });

  namespace Settings {
    export const basics = (title: string, timer: Timer) => ({
      title,
      dataIndex: timer,
    });

    export const sorting = ({ timer }: Timer) => ({
      defaultSortOrder: "descend",
      sorter: (a: Driver.Table.Datum, b: Driver.Table.Datum) =>
        Order.optionally(Order.number().compare)(
          a.hoursOfService?.timers?.[timer].timer.duration,
          b.hoursOfService?.timers?.[timer].timer.duration
        ),
    });

    export const rendering = (timer: Timer) => ({
      render: (_: unknown, datum: Driver.Table.Datum) => (
        <Timer {...timer} {...datum} />
      ),
    });
  }
}

export namespace Timers {
  export const columns = () =>
    [
      Timer.column("Cycle", { timer: "cycle" }),
      Timer.column("Shift", { timer: "shift" }),
      Timer.column("Drive", { timer: "drive" }),
      Timer.column("Break", { timer: "break" }),
    ] as const;
}
